import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight, BadgeCheck, ChevronRight } from "lucide-react";
import { Icon } from "@/components/common/Icon";
import { SCOPES } from "@/data/products";

const Crumbs = ({ name, dark }) => (
  <nav className={`flex items-center gap-1.5 text-xs ${dark ? "text-slate-400" : "text-slate-500"}`} data-testid="category-breadcrumb">
    <Link to="/solutions" className={dark ? "hover:text-white" : "hover:text-slate-900"}>Solutions</Link>
    <ChevronRight className="h-3 w-3" /> <span className={dark ? "text-white" : "text-slate-900"}>{name}</span>
  </nav>
);

const Chips = ({ items, dark }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((s) => (
      <span key={s} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${dark ? "border-white/20 text-slate-200" : "border-emerald-300 bg-emerald-50 text-emerald-800"}`}>
        <BadgeCheck className="h-3.5 w-3.5" /> {s}
      </span>
    ))}
  </div>
);

const Actions = ({ c, dark }) => (
  <div className="mt-8 flex flex-col gap-3 sm:flex-row">
    <a href="#products" className="btn-primary" data-testid="category-browse-button">Browse {c.products.length} products <ArrowDown className="h-4 w-4" /></a>
    <Link to="/enquiry" className={dark ? "inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10" : "btn-outline px-6 py-3"} data-testid="category-quote-button">
      Request a quote <ArrowRight className="h-4 w-4" />
    </Link>
  </div>
);

const Text = ({ c, index, dark }) => (
  <div>
    <Crumbs name={c.name} dark={dark} />
    <div className="mt-5"><span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${dark ? "bg-white/10 text-white" : "bg-[#123634] text-white"}`} data-testid="category-scope-badge">{SCOPES[c.scope]?.label}{c.scope === "wiring" ? " · wiring only" : ""}</span></div>
    <p className="mono mt-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em]" style={{ color: dark ? "#E8927F" : c.accent }}>
      <Icon name={c.icon} className="h-4 w-4" /> Category {String(index + 1).padStart(2, "0")}
    </p>
    <h1 className={`mt-3 text-4xl leading-[1.08] sm:text-5xl ${dark ? "text-white" : "text-slate-900"}`} data-testid="category-title">{c.name}</h1>
    <p className={`mt-5 max-w-xl text-base leading-relaxed md:text-lg ${dark ? "text-slate-300" : "text-slate-600"}`}>{c.intro}</p>
    <div className="mt-6"><Chips items={c.standards} dark={dark} /></div>
    <Actions c={c} dark={dark} />
  </div>
);

const Showcase = ({ c }) => (
  <div className="relative">
    <div className="absolute -inset-4 rounded-3xl opacity-20 blur-2xl" style={{ background: c.accent }} />
    <div className="relative grid grid-cols-2 gap-3">
      {c.products.slice(0, 3).map((p, i) => (
        <a key={p.id} href={`#${p.id}`} className={`overflow-hidden rounded-xl border border-slate-200 bg-white p-3 transition-transform duration-300 hover:-translate-y-1 ${i === 0 ? "row-span-2" : ""}`}>
          <img src={p.image} alt={p.name} className={`w-full object-contain mix-blend-multiply ${i === 0 ? "h-full min-h-[260px]" : "h-32"}`} />
        </a>
      ))}
    </div>
  </div>
);

export const CategoryHero = ({ c, index }) => {
  if (c.hero === "minimal") {
    return (
      <section className="border-b border-slate-200 bg-white" data-testid="category-hero-minimal">
        <div className="container-x py-14 lg:py-20">
          <div className="grid gap-10 lg:grid-cols-12">
            <span className="mono hidden text-[120px] font-medium leading-none text-slate-100 lg:col-span-2 lg:block">{String(index + 1).padStart(2, "0")}</span>
            <div className="lg:col-span-10"><Text c={c} index={index} /></div>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {c.products.map((p) => (
              <a key={p.id} href={`#${p.id}`} className="group rounded-xl border border-slate-200 bg-white p-3 transition-colors hover:border-slate-400" data-testid={`category-thumb-${p.id}`}>
                <img src={p.image} alt={p.name} className="h-24 w-full object-contain mix-blend-multiply transition-transform duration-300 group-hover:scale-105" />
                <p className="mt-2 truncate text-xs font-medium text-slate-700">{p.name}</p>
              </a>
            ))}
          </div>
        </div>
      </section>
    );
  }
  const dark = c.hero === "dark";
  return (
    <section className={`relative overflow-hidden ${dark ? "noise bg-[#123634]" : "bg-[#F4F6F2]"}`} data-testid={`category-hero-${c.hero}`}>
      {dark && <div className="grid-lines absolute inset-0 opacity-25" />}
      <div className="container-x relative grid gap-12 py-14 lg:grid-cols-2 lg:items-center lg:py-20">
        <Text c={c} index={index} dark={dark} />
        <Showcase c={c} />
      </div>
    </section>
  );
};
